import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { serviceSopAPI } from '../../services/api';
import {
  REPORT_STATUS, reportStatusKey, monthNo, fmtMD, fmtMDHM, toDate, toYMD, overText,
} from '../service/sopShared';

// 客户记录顶部的服务状态行 (VIP 服务流程 5.0):
//   签约第 N 天 · 每 N 小时联系 · 下次联系 · 待办 N · 投诉 N · N 月报告 · 状态
// 「每 N 小时联系」 opens the 调整联系频率 popover: a coach files a request
// (主管确认后生效), a 主管 sees the pending one with 确认 / 驳回.
//
// Props: status (GET /profiles/:id/service-status, or null → renders nothing),
// profileId, isZh, onReload, onOpenTodos, onOpenComplaints, onOpenReport(id).

const C = { ink: '#111111', sub: '#6E685F', mute: '#6E685F', line: 'rgba(17,17,17,.12)', sep: 'rgba(17,17,17,.12)', brand: '#111111', bad: '#9E2B25' };

const FREQ = [
  ['168', '每周一次', 'Once a week'],
  ['48', '48 小时（默认）', '48 hours (default)'],
  ['72', '72 小时', '72 hours'],
  ['pause', '暂停到某天', 'Pause until…'],
];
const freqText = (h, isZh) => {
  const n = Number(h) || 48;
  if (n === 168) return isZh ? '每周联系一次' : 'Weekly contact';
  return isZh ? `每 ${n} 小时联系` : `Contact every ${n}h`;
};

export default function ServiceStatusLine({ status, profileId, isZh = true, onReload, onOpenTodos, onOpenComplaints, onOpenReport }) {
  const t = (zh, en) => (isZh ? zh : en);
  const [open, setOpen] = useState(false);
  const [freq, setFreq] = useState('48');
  const [pauseUntil, setPauseUntil] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!status || typeof status !== 'object') return null;
  const s = status;
  const interval = Number(s.interval_hours) || 48;
  const pending = s.pending_interval_request || null;
  const todos = s.todos || {};
  const complaints = s.complaints || {};
  const report = s.report || null;
  const paused = s.pause_until && toDate(s.pause_until) && toDate(s.pause_until).getTime() >= new Date(new Date().toDateString()).getTime();
  const overdueH = s.overdue_hours != null && Number(s.overdue_hours) > 0 ? Number(s.overdue_hours) : null;

  const pieces = [];
  if (s.service_day != null) pieces.push(<span key="day">{t(`签约第 ${s.service_day} 天`, `Day ${s.service_day}`)}</span>);

  if (s.in_focus_period !== false) {
    pieces.push(
      <button key="freq" type="button" onClick={() => {
        setOpen((v) => !v);
        setFreq(String(interval === 168 || interval === 72 ? interval : 48));
        setPauseUntil(''); setReason('');
      }}
        title={t('调整联系频率', 'Change the contact frequency')}
        style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', color: C.brand, cursor: 'pointer', borderBottom: `1px dashed ${C.brand}` }}>
        {paused ? t(`暂停联系至 ${fmtMD(s.pause_until)}`, `Paused until ${fmtMD(s.pause_until)}`) : freqText(interval, isZh)}
        {pending && <span style={{ color: '#93591A', marginLeft: 4 }}>{t('· 待主管确认', '· pending')}</span>}
      </button>,
    );
    if (!paused) {
      if (overdueH != null) {
        pieces.push(<span key="next" style={{ color: C.bad, fontWeight: 500 }}>{t(`下次联系${overText(overdueH, true)}`, `Next contact ${overText(overdueH, false)}`)}</span>);
      } else if (s.next_due_at) {
        pieces.push(<span key="next">{t(`下次 ${fmtMDHM(s.next_due_at)}`, `Next ${fmtMDHM(s.next_due_at)}`)}</span>);
      }
    }
  } else {
    pieces.push(<span key="focus" style={{ color: C.mute }}>{t('已过 90 天重点期', 'Past the 90-day focus period')}</span>);
  }

  const todoN = Number(todos.open) || 0;
  const todoOver = Number(todos.overdue) || 0;
  pieces.push(
    <button key="todo" type="button" onClick={onOpenTodos}
      style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', cursor: 'pointer', color: todoOver ? C.bad : C.sub, fontWeight: todoOver ? 600 : 400 }}>
      {t(`待办 ${todoN}`, `To-dos ${todoN}`)}{todoOver ? t(`（逾期 ${todoOver}）`, ` (${todoOver} overdue)`) : ''}
    </button>,
  );
  const compN = Number(complaints.open) || 0;
  const urgent = Number(complaints.urgent_open) || 0;
  pieces.push(
    <button key="comp" type="button" onClick={onOpenComplaints}
      style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', cursor: onOpenComplaints ? 'pointer' : 'default', color: urgent ? '#FFFDF9' : C.sub, ...(urgent ? { background: '#9E2B25', borderRadius: 2, padding: '0 8px' } : {}) }}>
      {t(`投诉 ${compN}`, `Complaints ${compN}`)}{urgent ? t(` · 紧急 ${urgent}`, ` · ${urgent} urgent`) : ''}
    </button>,
  );
  if (report && report.id) {
    const rk = reportStatusKey(report);
    const rs = REPORT_STATUS[rk] || REPORT_STATUS.draft;
    const mon = monthNo(report);
    pieces.push(
      <button key="report" type="button" onClick={() => onOpenReport && onOpenReport(report.id)}
        style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', cursor: 'pointer', color: rk === 'overdue' ? C.bad : C.brand, fontWeight: 500 }}>
        {mon ? t(`${mon} 月报告`, `Month ${mon} report`) : t('月度报告', 'Report')} · {isZh ? rs.zh : rs.en}
      </button>,
    );
  }

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { toast.error(t('请填写原因', 'Give a reason')); return; }
    if (freq === 'pause' && !pauseUntil) { toast.error(t('请选择暂停到哪天', 'Pick a date')); return; }
    setBusy(true);
    try {
      await serviceSopAPI.requestInterval(profileId, freq === 'pause'
        ? { pause_until: pauseUntil, reason: reason.trim() }
        : { interval_hours: Number(freq), reason: reason.trim() });
      toast.success(t('已提交，主管确认后生效', 'Sent — takes effect once a supervisor approves'));
      setOpen(false);
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.error || t('提交失败', 'Could not submit'));
    } finally { setBusy(false); }
  };

  const review = async (action) => {
    if (!pending) return;
    let note = '';
    if (action === 'reject') {
      // eslint-disable-next-line no-alert
      const v = window.prompt(t('驳回原因（选填）', 'Reason (optional)'), '');
      if (v === null) return;
      note = v;
    }
    setBusy(true);
    try {
      await serviceSopAPI.reviewInterval(pending.id, { action, note: note || undefined });
      toast.success(action === 'approve' ? t('已确认，频率已生效', 'Approved') : t('已驳回', 'Rejected'));
      setOpen(false);
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.error || t('操作失败', 'Failed'));
    } finally { setBusy(false); }
  };

  const field = { font: 'inherit', fontSize: 13.5, border: '1px solid rgba(17,17,17,.12)', borderRadius: 2, padding: '6px 9px', background: '#FFFDF9', minWidth: 0, boxSizing: 'border-box', width: '100%' };
  const pendingText = pending ? (pending.pause_until
    ? t(`暂停联系到 ${fmtMD(pending.pause_until)}`, `Pause until ${fmtMD(pending.pause_until)}`)
    : freqText(pending.interval_hours, isZh)) : '';

  return (
    <div ref={wrapRef} style={{ position: 'relative', marginTop: 6 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px', fontSize: 12, color: C.sub, alignItems: 'center', lineHeight: 1.6 }}>
        {pieces.map((p, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span aria-hidden="true" style={{ color: C.sep }}>|</span>}
            {p}
          </React.Fragment>
        ))}
      </div>

      {open && (
        <div role="dialog" aria-label={t('调整联系频率', 'Contact frequency')}
          style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 40, width: 'min(320px, 86vw)', background: '#FFFDF9', borderRadius: 2, boxShadow: 'none', border: `1px solid ${C.line}`, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, color: C.ink }}>
          <div style={{ fontWeight: 500, fontSize: 14.5 }}>{t('调整联系频率', 'Contact frequency')}</div>
          {pending ? (
            <>
              <div style={{ fontSize: 13, lineHeight: 1.6, background: '#F3EADB', color: '#93591A', borderRadius: 2, padding: '8px 10px' }}>
                <b style={{ fontWeight: 500 }}>{t('待主管确认：', 'Pending: ')}</b>{pendingText}
                {pending.reason ? <div style={{ color: C.ink }}>「{pending.reason}」</div> : null}
                <div style={{ fontSize: 12, color: C.sub }}>{[pending.requested_by_name, fmtMDHM(pending.created_at)].filter(Boolean).join(' · ')}</div>
              </div>
              {s.can_approve ? (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button type="button" disabled={busy} onClick={() => review('reject')}
                    style={{ height: 32, padding: '0 14px', borderRadius: 2, border: '1px solid rgba(17,17,17,.12)', background: '#FFFDF9', font: 'inherit', fontSize: 13, cursor: 'pointer' }}>{t('驳回', 'Reject')}</button>
                  <button type="button" disabled={busy} onClick={() => review('approve')}
                    style={{ height: 32, padding: '0 14px', borderRadius: 2, border: 'none', background: C.brand, color: '#FFFDF9', font: 'inherit', fontSize: 13, cursor: 'pointer' }}>{t('确认', 'Approve')}</button>
                </div>
              ) : (
                <div style={{ fontSize: 12.5, color: C.sub }}>{t('主管确认前，仍按当前频率计算。', 'The current frequency applies until a supervisor approves.')}</div>
              )}
            </>
          ) : (
            <form onSubmit={submitRequest} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={{ fontSize: 12.5, color: C.sub, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {t('频率', 'Frequency')}
                <select value={freq} onChange={(e) => setFreq(e.target.value)} style={field}>
                  {FREQ.map(([v, zh, en]) => <option key={v} value={v}>{isZh ? zh : en}</option>)}
                </select>
              </label>
              {freq === 'pause' && (
                <label style={{ fontSize: 12.5, color: C.sub, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {t('暂停到', 'Pause until')}
                  <input type="date" value={pauseUntil} min={toYMD(new Date())} onChange={(e) => setPauseUntil(e.target.value)} style={field} />
                </label>
              )}
              <label style={{ fontSize: 12.5, color: C.sub, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {t('原因', 'Reason')}
                <input type="text" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)}
                  placeholder={t('例如：会员出差两周，要求每周联系一次', 'e.g. travelling for two weeks, asked for weekly contact')} style={field} />
              </label>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#93591A' }}>{t(`主管确认后生效，之前仍按 ${interval} 小时计算`, `Takes effect after approval; ${interval}h until then`)}</span>
                <button type="submit" disabled={busy}
                  style={{ height: 32, padding: '0 14px', borderRadius: 2, border: 'none', background: C.brand, color: '#FFFDF9', font: 'inherit', fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}>
                  {busy ? t('提交中…', 'Sending…') : t('提交申请', 'Send request')}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
